import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, Clock, Calendar, ArrowRight, HelpCircle, Sparkles, Share2, MessageCircle, Facebook, Link2, Check, Mail, ArrowUp, Bookmark, BookmarkCheck, Type, List, Bot } from "lucide-react";

const SAVED_KEY = "raf_blog_saved_v1";
const READ_MODE_KEY = "raf_blog_reading_mode_v1";

// Helpers de favoritos (localStorage)
export function getSavedPosts() {
  try {
    const raw = localStorage.getItem(SAVED_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}
function setSavedPosts(arr) {
  try { localStorage.setItem(SAVED_KEY, JSON.stringify(arr)); } catch { /* silent */ }
}

const CAT_COLORS = {
  "Dependência Química": "bg-rose-100 text-rose-800 border-rose-200",
  "Saúde Mental": "bg-violet-100 text-violet-800 border-violet-200",
  "Tratamento": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Clínicas de Recuperação": "bg-sky-100 text-sky-800 border-sky-200",
  "Profissionais RAF": "bg-amber-100 text-amber-800 border-amber-200",
};

// Slug de categoria pra passar no CTA da LIA (mesma normalização do backend)
const CAT_SLUGS = {
  "Dependência Química": "dependencia-quimica",
  "Saúde Mental": "saude-mental",
  "Tratamento": "tratamento",
  "Clínicas de Recuperação": "clinicas-de-recuperacao",
  "Profissionais RAF": "profissionais-raf",
};

// Slugify pra âncoras do sumário
function slugifyHeading(s) {
  return (s || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60) || "sec";
}

// Markdown → HTML leve + coleta H2s pra sumário (TOC)
function renderMarkdown(md) {
  if (!md) return { html: "", toc: [] };
  const lines = md.split(/\r?\n/);
  const out = [];
  const toc = [];
  const usedIds = new Set();
  let listOpen = false;
  const flushList = () => { if (listOpen) { out.push("</ul>"); listOpen = false; } };
  const inline = (s) => s
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, "<code class='bg-slate-100 px-1 rounded'>$1</code>");
  const uniqueId = (base) => {
    let id = base, i = 2;
    while (usedIds.has(id)) { id = `${base}-${i++}`; }
    usedIds.add(id);
    return id;
  };
  for (const raw of lines) {
    const l = raw.trim();
    if (!l) { flushList(); continue; }
    if (l.startsWith("### ")) { flushList(); out.push(`<h3 class='text-lg font-bold text-[#0B2B62] mt-6 mb-2'>${inline(l.slice(4))}</h3>`); continue; }
    if (l.startsWith("## ")) {
      flushList();
      const rawText = l.slice(3);
      const id = uniqueId(slugifyHeading(rawText));
      toc.push({ id, text: rawText });
      out.push(`<h2 id='${id}' class='text-2xl font-bold text-[#0B2B62] mt-8 mb-3 scroll-mt-24'>${inline(rawText)}</h2>`);
      continue;
    }
    if (l.startsWith("# ")) { flushList(); out.push(`<h1 class='text-3xl font-bold text-[#0B2B62] mt-8 mb-4'>${inline(l.slice(2))}</h1>`); continue; }
    if (l.startsWith("- ") || l.startsWith("* ")) {
      if (!listOpen) { out.push("<ul class='list-disc pl-5 space-y-1 my-3 text-slate-700'>"); listOpen = true; }
      out.push(`<li>${inline(l.slice(2))}</li>`);
      continue;
    }
    flushList();
    out.push(`<p class='text-slate-700 leading-relaxed my-3'>${inline(l)}</p>`);
  }
  flushList();
  return { html: out.join(""), toc };
}

// Injeta/atualiza tags SEO no <head>
function upsertMeta(sel, attrs) {
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("meta");
    for (const [k, v] of Object.entries(attrs.create || {})) el.setAttribute(k, v);
    document.head.appendChild(el);
  }
  for (const [k, v] of Object.entries(attrs.set || {})) el.setAttribute(k, v);
}
function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel='${rel}']`);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
}
function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = id; document.head.appendChild(el); }
  el.textContent = JSON.stringify(data);
}

export default function PublicBlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [copied, setCopied] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [readingMode, setReadingMode] = useState(() => {
    try { return localStorage.getItem(READ_MODE_KEY) === "1"; } catch { return false; }
  });
  const [isSaved, setIsSaved] = useState(false);

  // Sincroniza estado "salvo" quando o post carrega
  useEffect(() => {
    if (!post) return;
    const saved = getSavedPosts();
    setIsSaved(saved.some(s => s.slug === post.slug));
  }, [post]);

  const toggleReadingMode = () => {
    setReadingMode(v => {
      const nv = !v;
      try { localStorage.setItem(READ_MODE_KEY, nv ? "1" : "0"); } catch { /* silent */ }
      toast.success(nv ? "Modo leitura confortável ativado" : "Voltou ao tamanho padrão");
      return nv;
    });
  };

  const toggleSaved = () => {
    if (!post) return;
    const saved = getSavedPosts();
    const exists = saved.some(s => s.slug === post.slug);
    let next;
    if (exists) {
      next = saved.filter(s => s.slug !== post.slug);
      toast.success("Removido dos salvos");
    } else {
      next = [
        {
          slug: post.slug,
          title: post.title,
          excerpt: post.excerpt,
          category: post.category,
          hero_image: post.hero_image,
          read_minutes: post.read_minutes,
          saved_at: new Date().toISOString(),
        },
        ...saved,
      ].slice(0, 200);
      toast.success("Artigo salvo — veja em /blog/salvos");
    }
    setSavedPosts(next);
    setIsSaved(!exists);
  };

  // Progresso de leitura + gates pra sidebar / back-to-top
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const doc = document.documentElement;
        const total = Math.max(1, doc.scrollHeight - window.innerHeight);
        const p = Math.min(100, Math.max(0, (window.scrollY / total) * 100));
        setScrollProgress(p);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [post]);

  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const load = useCallback(async () => {
    setLoading(true); setNotFound(false);
    try {
      const r = await api.get(`/public/blog/${slug}`);
      setPost(r.data);
      window.scrollTo({ top: 0, behavior: "instant" });
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
    } finally { setLoading(false); }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  // v0.14 — Injeta SEO tags dinamicamente
  useEffect(() => {
    if (!post) return;
    const pageUrl = `${window.location.origin}/blog/${post.slug}`;
    const title = `${post.title} · Blog RAF Direct`;
    const desc = (post.excerpt || "").replace(/\s+/g, " ").slice(0, 160);
    const image = post.hero_image || `${window.location.origin}/og-image.jpg`;

    const originalTitle = document.title;
    document.title = title;
    upsertMeta("meta[name='description']", { create: { name: "description" }, set: { content: desc } });
    // Open Graph
    upsertMeta("meta[property='og:title']", { create: { property: "og:title" }, set: { content: post.title } });
    upsertMeta("meta[property='og:description']", { create: { property: "og:description" }, set: { content: desc } });
    upsertMeta("meta[property='og:image']", { create: { property: "og:image" }, set: { content: image } });
    upsertMeta("meta[property='og:url']", { create: { property: "og:url" }, set: { content: pageUrl } });
    upsertMeta("meta[property='og:type']", { create: { property: "og:type" }, set: { content: "article" } });
    upsertMeta("meta[property='article:section']", { create: { property: "article:section" }, set: { content: post.category || "" } });
    upsertMeta("meta[property='article:published_time']", { create: { property: "article:published_time" }, set: { content: post.created_at || "" } });
    // Twitter card
    upsertMeta("meta[name='twitter:card']", { create: { name: "twitter:card" }, set: { content: "summary_large_image" } });
    upsertMeta("meta[name='twitter:title']", { create: { name: "twitter:title" }, set: { content: post.title } });
    upsertMeta("meta[name='twitter:description']", { create: { name: "twitter:description" }, set: { content: desc } });
    upsertMeta("meta[name='twitter:image']", { create: { name: "twitter:image" }, set: { content: image } });
    // Canonical
    upsertLink("canonical", pageUrl);
    // JSON-LD BlogPosting
    upsertJsonLd("jsonld-blog-post", {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": post.title,
      "description": desc,
      "image": [image, ...(post.gallery || [])].filter(Boolean),
      "author": { "@type": "Organization", "name": post.author || "RAF Direct" },
      "publisher": { "@type": "Organization", "name": "RAF Direct", "logo": { "@type": "ImageObject", "url": `${window.location.origin}/raf-logo.png` } },
      "datePublished": post.created_at,
      "dateModified": post.updated_at || post.created_at,
      "articleSection": post.category,
      "mainEntityOfPage": { "@type": "WebPage", "@id": pageUrl },
    });
    // JSON-LD FAQPage (só se houver perguntas)
    if (post.faq && post.faq.length > 0) {
      upsertJsonLd("jsonld-faq", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": post.faq.map(f => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      });
    }
    return () => { document.title = originalTitle; };
  }, [post]);

  const shareLinks = useMemo(() => {
    if (!post) return null;
    const pageUrl = `${window.location.origin}/blog/${post.slug}`;
    const text = `${post.title} — Blog RAF Direct`;
    const emailBody = `Achei este artigo interessante e quis compartilhar com você:\n\n${post.title}\n\n${post.excerpt || ""}\n\nLeia aqui: ${pageUrl}\n\n— RAF Direct`;
    return {
      url: pageUrl,
      wa: `https://wa.me/?text=${encodeURIComponent(text + "\n" + pageUrl)}`,
      fb: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`,
      email: `mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(emailBody)}`,
    };
  }, [post]);

  const copyLink = async () => {
    if (!shareLinks) return;
    try {
      await navigator.clipboard.writeText(shareLinks.url);
      setCopied(true);
      toast.success("Link copiado");
      setTimeout(() => setCopied(false), 2500);
    } catch { toast.error("Não foi possível copiar"); }
  };

  const openLiaContextual = () => {
    if (!post) return;
    const topic = CAT_SLUGS[post.category] || "";
    // Passa blog_topic + auto-abrir widget quando chegar em /
    sessionStorage.setItem("lia_auto_open", "1");
    navigate(topic ? `/?blog_topic=${topic}` : "/");
  };

  if (loading) return <div className="min-h-screen bg-slate-50 py-20 text-center text-slate-400 text-sm">Carregando...</div>;
  if (notFound) return (
    <div className="min-h-screen bg-slate-50 py-20 text-center">
      <div className="text-2xl font-bold text-[#0B2B62] mb-2">Artigo não encontrado</div>
      <Link to="/blog" className="text-sm text-[#0B2B62] underline">Voltar pro blog</Link>
    </div>
  );
  if (!post) return null;

  const catColor = CAT_COLORS[post.category] || "bg-slate-100 text-slate-700 border-slate-200";
  const { html, toc } = renderMarkdown(post.content);
  const created = new Date(post.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
  const heroFallback = `https://picsum.photos/seed/${post.id || post.slug}/1600/900`;
  const galleryFallback = (i) => `https://picsum.photos/seed/${(post.id||post.slug)+"-g"+i}/1200/900`;

  return (
    <article className="min-h-screen bg-white" data-testid="public-blog-post">
      <div className="relative w-full h-[38vh] sm:h-[50vh] overflow-hidden">
        <img src={post.hero_image || heroFallback} alt={post.title}
             onError={e=>{ if(e.target.dataset.fbk!=='1'){e.target.dataset.fbk='1'; e.target.src=heroFallback;} }}
             className="w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"/>
        <div className="absolute inset-x-0 bottom-0 max-w-4xl mx-auto px-4 pb-8 text-white">
          <Link to="/blog" className="inline-flex items-center gap-1 text-xs opacity-90 hover:opacity-100 mb-3" data-testid="blog-back"><ChevronLeft className="w-3.5 h-3.5"/> Voltar pro Blog</Link>
          <div className="flex items-center gap-2 mb-3">
            <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full border ${catColor}`}>{post.category}</span>
            <span className="text-xs opacity-80 flex items-center gap-1"><Clock className="w-3 h-3"/>{post.read_minutes} min</span>
            <span className="text-xs opacity-80 flex items-center gap-1"><Calendar className="w-3 h-3"/>{created}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight" data-testid="blog-post-title">{post.title}</h1>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-10 relative">
        {/* Share toolbar fixa na lateral esquerda — só desktop grande + só depois de 30% de leitura */}
        {shareLinks && (
          <div
            className={`hidden xl:flex flex-col gap-2 fixed left-4 top-1/3 z-30 transition-all duration-500 ${
              scrollProgress > 30 ? "opacity-100 translate-x-0 pointer-events-auto" : "opacity-0 -translate-x-8 pointer-events-none"
            }`}
            data-testid="blog-share-toolbar"
          >
            <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1 mb-1"><Share2 className="w-3 h-3"/> Compartilhar</div>
            <a href={shareLinks.wa} target="_blank" rel="noreferrer" title="WhatsApp" data-testid="blog-share-wa" className="w-10 h-10 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center transition shadow-md"><MessageCircle className="w-5 h-5"/></a>
            <a href={shareLinks.fb} target="_blank" rel="noreferrer" title="Facebook" data-testid="blog-share-fb" className="w-10 h-10 rounded-full bg-[#1877F2] hover:bg-[#0e6cd8] text-white flex items-center justify-center transition shadow-md"><Facebook className="w-5 h-5"/></a>
            <a href={shareLinks.email} title="Enviar por email" data-testid="blog-share-email" className="w-10 h-10 rounded-full bg-slate-700 hover:bg-slate-800 text-white flex items-center justify-center transition shadow-md"><Mail className="w-5 h-5"/></a>
            <button onClick={copyLink} title="Copiar link" data-testid="blog-share-copy" className="w-10 h-10 rounded-full bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 flex items-center justify-center transition shadow-md">{copied ? <Check className="w-5 h-5 text-emerald-600"/> : <Link2 className="w-5 h-5"/>}</button>
          </div>
        )}

        {/* Barra horizontal de compartilhamento — SEMPRE visível abaixo do excerpt (mobile + desktop médio) */}
        {shareLinks && (
          <div className="xl:hidden mb-6 flex items-center gap-2 flex-wrap" data-testid="blog-share-top">
            <span className="text-xs text-slate-500 font-bold mr-1 inline-flex items-center gap-1"><Share2 className="w-3 h-3"/> Compartilhar:</span>
            <a href={shareLinks.wa} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold inline-flex items-center gap-1 transition"><MessageCircle className="w-3.5 h-3.5"/> WhatsApp</a>
            <a href={shareLinks.fb} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-lg bg-[#1877F2] hover:bg-[#0e6cd8] text-white text-xs font-bold inline-flex items-center gap-1 transition"><Facebook className="w-3.5 h-3.5"/> Facebook</a>
            <a href={shareLinks.email} className="px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-800 text-white text-xs font-bold inline-flex items-center gap-1 transition"><Mail className="w-3.5 h-3.5"/> Email</a>
            <button onClick={copyLink} className="px-3 py-1.5 rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-bold inline-flex items-center gap-1 transition">{copied ? <><Check className="w-3.5 h-3.5"/> Copiado</> : <><Link2 className="w-3.5 h-3.5"/> Copiar link</>}</button>
          </div>
        )}

        <p className="text-lg text-slate-700 leading-relaxed mb-8 border-l-4 border-[#0B2B62] pl-4" data-testid="blog-post-excerpt">{post.excerpt}</p>

        {/* Sumário automático + Modo Leitura Confortável + Salvar */}
        <div className="mb-8 flex flex-col md:flex-row gap-3 md:items-start" data-testid="blog-toolbar">
          {toc.length > 1 && (
            <details className="flex-1 border border-slate-200 rounded-xl bg-slate-50/60 open:bg-white transition" data-testid="blog-toc">
              <summary className="cursor-pointer px-4 py-3 flex items-center gap-2 font-semibold text-sm text-[#0B2B62] hover:bg-slate-50 rounded-xl">
                <List className="w-4 h-4"/> Sumário do artigo <span className="text-xs text-slate-400 font-normal">({toc.length} tópicos)</span>
              </summary>
              <nav className="px-4 pb-3 pt-1">
                <ol className="text-sm space-y-1.5 list-decimal list-inside marker:text-slate-400" data-testid="blog-toc-list">
                  {toc.map(t => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-slate-700 hover:text-[#1E4FB8] hover:underline" data-testid={`blog-toc-item-${t.id}`}>{t.text}</a>
                    </li>
                  ))}
                </ol>
              </nav>
            </details>
          )}
          <div className="flex gap-2 shrink-0">
            <button onClick={toggleReadingMode} data-testid="blog-reading-mode-toggle" title="Modo leitura confortável (fonte maior)" className={`px-3 py-2 rounded-xl text-xs font-bold inline-flex items-center gap-1.5 border transition ${readingMode ? "bg-[#0B2B62] text-white border-[#0B2B62]" : "bg-white text-[#0B2B62] border-slate-200 hover:border-slate-400"}`}>
              <Type className="w-4 h-4"/> {readingMode ? "Fonte grande" : "Aumentar fonte"}
            </button>
            <button onClick={toggleSaved} data-testid="blog-save-toggle" title={isSaved ? "Remover dos salvos" : "Salvar para depois"} className={`px-3 py-2 rounded-xl text-xs font-bold inline-flex items-center gap-1.5 border transition ${isSaved ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>
              {isSaved ? <><BookmarkCheck className="w-4 h-4"/> Salvo</> : <><Bookmark className="w-4 h-4"/> Salvar</>}
            </button>
          </div>
        </div>

        <div
          className={`prose max-w-none text-slate-700 transition-all ${readingMode ? "text-lg leading-loose reading-mode" : ""}`}
          dangerouslySetInnerHTML={{ __html: html }}
          data-testid="blog-post-content"
        />

        {/* Share buttons inline (mobile + desktop backup) */}
        {shareLinks && (
          <div className="mt-8 hidden xl:flex items-center gap-2 flex-wrap" data-testid="blog-share-bottom">
            <span className="text-xs text-slate-500 font-bold mr-1">Gostou? Compartilhe:</span>
            <a href={shareLinks.wa} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold inline-flex items-center gap-1"><MessageCircle className="w-3.5 h-3.5"/> WhatsApp</a>
            <a href={shareLinks.fb} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-lg bg-[#1877F2] hover:bg-[#0e6cd8] text-white text-xs font-bold inline-flex items-center gap-1"><Facebook className="w-3.5 h-3.5"/> Facebook</a>
            <button onClick={copyLink} className="px-3 py-1.5 rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-bold inline-flex items-center gap-1">{copied ? <><Check className="w-3.5 h-3.5"/> Copiado</> : <><Link2 className="w-3.5 h-3.5"/> Copiar link</>}</button>
          </div>
        )}

        {post.gallery && post.gallery.length > 0 && (
          <div className="mt-10">
            <h2 className="text-lg font-bold text-[#0B2B62] mb-4">Imagens do tema</h2>
            <div className="grid sm:grid-cols-3 gap-3" data-testid="blog-gallery">
              {post.gallery.map((src, i) => (
                <a key={i} href={src} target="_blank" rel="noreferrer" data-testid={`blog-gallery-${i}`} className="block aspect-[4/3] overflow-hidden rounded-xl bg-slate-100">
                  <img src={src || galleryFallback(i)} alt={`${post.title} — imagem ${i+1}`} loading="lazy"
                       onError={e=>{ if(e.target.dataset.fbk!=='1'){e.target.dataset.fbk='1'; e.target.src=galleryFallback(i);} }}
                       className="w-full h-full object-cover hover:scale-105 transition duration-500"/>
                </a>
              ))}
            </div>
          </div>
        )}

        {post.faq && post.faq.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-[#0B2B62] mb-4 flex items-center gap-2"><HelpCircle className="w-5 h-5"/> Perguntas frequentes</h2>
            <div className="space-y-2" data-testid="blog-faq">
              {post.faq.map((f, i) => (
                <div key={i} className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`blog-faq-${i}`}>
                  <button onClick={()=>setOpenFaq(openFaq===i?null:i)} className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50" data-testid={`blog-faq-toggle-${i}`}>
                    <span className="font-semibold text-slate-800">{f.q}</span>
                    <span className="text-[#0B2B62] text-xl leading-none">{openFaq===i?"−":"+"}</span>
                  </button>
                  {openFaq===i && <div className="px-4 pb-4 text-slate-700 text-sm leading-relaxed">{f.a}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-12 rounded-2xl bg-gradient-to-br from-[#0B2B62] to-[#1E4FB8] text-white p-6 sm:p-8 flex items-center justify-between gap-4 flex-wrap" data-testid="blog-cta-lia">
          <div>
            <div className="flex items-center gap-2 text-xs opacity-80 uppercase tracking-widest"><Sparkles className="w-3.5 h-3.5"/> Precisa de ajuda agora?</div>
            <div className="text-xl font-bold mt-1">Converse com a LIA em segundos</div>
            <div className="text-sm opacity-90 mt-1">Nossa Inteligência de Acolhimento entende seu momento e direciona o próximo passo com cuidado.</div>
          </div>
          <button onClick={openLiaContextual} data-testid="blog-cta-lia-btn" className="px-5 py-3 rounded-xl bg-white text-[#0B2B62] font-bold hover:bg-slate-100 transition inline-flex items-center gap-2">Abrir a LIA <ArrowRight className="w-4 h-4"/></button>
        </div>

        {/* Sugerido pela LIA — pega o primeiro related da mesma categoria e destaca */}
        {post.related && post.related.length > 0 && (() => {
          const sugg = post.related.find(r => r.category === post.category) || post.related[0];
          const why = sugg.category === post.category
            ? `Como você acabou de ler sobre ${post.category.toLowerCase()}, este próximo passo aprofunda o mesmo tema.`
            : `Se este artigo tocou você, o próximo tema conversa direto com ele — em uma perspectiva complementar.`;
          const rf = `https://picsum.photos/seed/${sugg.slug}/1200/675`;
          return (
            <div className="mt-10 rounded-2xl border-2 border-violet-200 bg-gradient-to-br from-violet-50 to-white p-5 sm:p-6" data-testid="blog-lia-suggestion">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 rounded-full bg-violet-600 text-white flex items-center justify-center shrink-0"><Bot className="w-5 h-5"/></div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-violet-700 font-bold">LIA sugere pra você</div>
                  <div className="text-xs text-slate-600">{why}</div>
                </div>
              </div>
              <Link to={`/blog/${sugg.slug}`} className="flex gap-4 items-center bg-white rounded-xl border border-slate-200 hover:border-violet-400 hover:shadow-md transition p-3" data-testid="blog-lia-suggestion-link">
                <div className="w-24 h-16 sm:w-32 sm:h-20 rounded-lg overflow-hidden bg-slate-100 shrink-0">
                  <img src={sugg.hero_image || rf} alt={sugg.title} loading="lazy"
                       onError={e=>{ if(e.target.dataset.fbk!=='1'){e.target.dataset.fbk='1'; e.target.src=rf;} }}
                       className="w-full h-full object-cover"/>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-violet-700 mb-1">{sugg.category}</div>
                  <div className="text-sm sm:text-base font-bold text-[#0B2B62] leading-tight line-clamp-2">{sugg.title}</div>
                </div>
                <ArrowRight className="w-5 h-5 text-violet-600 shrink-0"/>
              </Link>
            </div>
          );
        })()}

        {post.related && post.related.length > 0 && (
          <div className="mt-14">
            <h2 className="text-2xl font-bold text-[#0B2B62] mb-4">Continue lendo</h2>
            <div className="grid sm:grid-cols-3 gap-4" data-testid="blog-related">
              {post.related.map(r => {
                const rf = `https://picsum.photos/seed/${r.slug}/800/450`;
                return (
                  <Link key={r.slug} to={`/blog/${r.slug}`} className="group border border-slate-200 rounded-xl overflow-hidden hover:border-slate-400 hover:shadow-md transition" data-testid={`blog-related-${r.slug}`}>
                    <div className="aspect-[16/10] bg-slate-100 overflow-hidden">
                      <img src={r.hero_image || rf} alt={r.title} loading="lazy"
                           onError={e=>{ if(e.target.dataset.fbk!=='1'){e.target.dataset.fbk='1'; e.target.src=rf;} }}
                           className="w-full h-full object-cover group-hover:scale-105 transition duration-500"/>
                    </div>
                    <div className="p-3">
                      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">{r.category}</div>
                      <div className="text-sm font-bold text-[#0B2B62] leading-tight group-hover:text-[#1E4FB8] transition line-clamp-2">{r.title}</div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </article>
  );
}
